// Desktop local capture producer: implements `LocalCaptureProducer` against the existing
// `window.sidetoneDesktop.capture` bridge and the encrypted spool behind it.
//
// - Starts only when the owner-supplied, server-verified policy says the call is recording, consent is
//   verified and `desktop.capture` is allowed and enabled (see policy.ts). The spool checks the
//   server again on `begin`. Nothing here can turn capture on by itself.
// - Records the caller-owned microphone and remote tracks through a WebAudio graph (graph.ts) with
//   MediaRecorder `audio/webm;codecs=opus`. Caller-owned tracks are never stopped.
// - One ordered queue carries chunks and control markers (sources, pause, resume), so the spool sees
//   them in recording order. Appends are strictly sequential; `capture_backpressure` retries the same
//   sequence number with the same bytes. Queue bytes are bounded.
// - Every run is fenced by call, identity and bridge session. A late event or result from an old
//   run can only reach that run's own session, and a run stops on sign-out, identity change, policy
//   stop, end of call or `capture_stopped`.
// - An item that ends before the call does, or that lost queued audio, is marked with a final source
//   report of no sources, so the spool can never present it as full coverage.
// Audio stays inside the desktop bridge. This module makes no network requests.

import type { AuthState, CaptureBeginResult, CaptureEvent, SidetoneDesktopBridge, SpoolItemView, Unsubscribe } from '../../../../apps/desktop/src/shared/bridge.js';
import type { CaptureCoverage, CaptureSources, LocalCaptureProducer } from '../../../../apps/desktop/src/shared/capture.js';
import { coverageLabel } from './coverage.js';
import { createWebAudioGraph, type CaptureGraph, type CaptureGraphFactory } from './graph.js';
import { decide, isValidPolicyInput, sameIdentity, type CaptureIdentity, type DesktopCapturePolicyInput, type PolicyDenyReason } from './policy.js';

export const PRODUCER_MIME = 'audio/webm;codecs=opus' as const;

export type CaptureBridge = SidetoneDesktopBridge['capture'];
export type CaptureAuthEvents = Pick<SidetoneDesktopBridge['auth'], 'onChange'>;

/** The parts of MediaRecorder the producer uses. */
export interface RecorderLike extends EventTarget {
  readonly state: 'inactive' | 'recording' | 'paused';
  start(timeslice?: number): void;
  stop(): void;
  pause(): void;
  resume(): void;
  requestData(): void;
}
export type RecorderFactory = (stream: MediaStream, options: MediaRecorderOptions) => RecorderLike;

/** A source track as the producer sees it; real `MediaStreamTrack`s satisfy this. */
export type SourceTrack = MediaStreamTrack;

export interface ProducerLimits {
  /** MediaRecorder timeslice. */
  timesliceMs: number;
  audioBitsPerSecond: number;
  /** Encoded bytes allowed to wait in the renderer before capture fails rather than grow. */
  maxQueuedBytes: number;
  /** Queue entries (chunks plus markers) allowed to wait. */
  maxQueuedItems: number;
  /** Largest chunk the producer sends, further capped by the spool's `max_chunk_bytes`. */
  maxChunkBytes: number;
  backoffInitialMs: number;
  backoffMaxMs: number;
  /** Total backpressure wait for one chunk before the bridge is treated as stalled. */
  maxBackpressureWaitMs: number;
  /** Wait for the recorder's final chunk on stop. */
  stopFlushTimeoutMs: number;
  /** Wait for queued chunks to reach the spool at the end of a call. */
  endDrainTimeoutMs: number;
  /** Fallback when MediaRecorder never fires `pause`. */
  pauseEventTimeoutMs: number;
}

export const DEFAULT_LIMITS: ProducerLimits = {
  timesliceMs: 1000,
  audioBitsPerSecond: 48_000,
  maxQueuedBytes: 4 * 1024 * 1024,
  maxQueuedItems: 2048,
  maxChunkBytes: 256 * 1024,
  backoffInitialMs: 50,
  backoffMaxMs: 1000,
  maxBackpressureWaitMs: 30_000,
  stopFlushTimeoutMs: 5000,
  endDrainTimeoutMs: 15_000,
  pauseEventTimeoutMs: 2000,
};

export interface Timers {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

export interface ProducerDeps {
  capture: CaptureBridge;
  auth?: CaptureAuthEvents;
  createGraph?: CaptureGraphFactory;
  createRecorder?: RecorderFactory;
  isTypeSupported?: (mime: string) => boolean;
  limits?: Partial<ProducerLimits>;
  timers?: Timers;
}

export type ProducerPhase = 'idle' | 'starting' | 'capturing' | 'paused' | 'stopping' | 'stopped' | 'denied' | 'unavailable' | 'failed';

export interface ProducerState {
  phase: ProducerPhase;
  call_id: string | null;
  /** Why the producer is denied, unavailable, stopped or failed. */
  reason: string | null;
  item_id: string | null;
  /** Coverage as last reported by the spool; never computed or upgraded here. */
  coverage: CaptureCoverage | null;
  coverage_label: string | null;
  sources: CaptureSources;
  /** True when this producer marked the item as cut short (ended early or lost queued audio). */
  truncated: boolean;
  /** Final spool view after finalize; null while running, after abort or when main stopped it. */
  item: SpoolItemView | null;
  stats: { chunks_sent: number; bytes_sent: number; backpressure_retries: number; dropped_bytes: number; max_queued_bytes: number };
  /** Last refused request (for example a resume the server state does not allow). */
  refused: string | null;
}

type Marker = { kind: 'pause' } | { kind: 'resume' } | { kind: 'sources'; worst: CaptureSources; last: CaptureSources };
type QueueItem = { kind: 'chunk'; blob: Blob } | Marker;

interface Plan {
  /** Let queued audio reach the spool before finalizing. */
  drain: boolean;
  /** The item ends before the call does. */
  truncated: boolean;
  outcome: 'stopped' | 'failed';
  reason: string;
}

interface Run {
  readonly callId: string;
  readonly identity: CaptureIdentity;
  sessionId: string | null;
  itemId: string | null;
  maxChunkBytes: number;
  graph: CaptureGraph | null;
  recorder: RecorderLike | null;
  recorderStarted: boolean;
  recorderDone: boolean;
  queue: QueueItem[];
  queuedBytes: number;
  wantPaused: boolean;
  bridgePaused: boolean;
  pendingPause: { fire: () => void } | null;
  plan: Plan | null;
  halt: boolean;
  sessionGone: boolean;
  bridgeFailure: string | null;
  acceptedChunks: number;
  nextSeq: number;
  droppedBytes: number;
  /** Set once start() has a session and hands termination over to finish(). */
  started: boolean;
  finishing: boolean;
  pumping: boolean;
  pumpDone: Promise<void>;
  wake: (() => void) | null;
  idleWaiters: Set<() => void>;
  drainWaiters: Set<() => void>;
  cleanups: (() => void)[];
  done: Promise<void>;
  resolveDone: () => void;
}

const DESKTOP_STOPPED_CODES = new Set(['capture_stopped', 'capture_no_session', 'capture_quota_full', 'capture_disk_low', 'capture_write_failed', 'capture_unavailable']);
const NO_SOURCES: CaptureSources = Object.freeze({ local_microphone: false, remote_audio: false });

function codeOf(err: unknown): string {
  const code = err && typeof err === 'object' ? (err as { code?: unknown }).code : undefined;
  return typeof code === 'string' && code ? code : 'internal';
}
const sameSources = (a: CaptureSources, b: CaptureSources) => a.local_microphone === b.local_microphone && a.remote_audio === b.remote_audio;
const andSources = (a: CaptureSources, b: CaptureSources): CaptureSources => ({ local_microphone: a.local_microphone && b.local_microphone, remote_audio: a.remote_audio && b.remote_audio });

export class DesktopCaptureProducer implements LocalCaptureProducer {
  private readonly limits: ProducerLimits;
  private readonly timers: Timers;
  private readonly createGraph: CaptureGraphFactory;
  private readonly createRecorder: RecorderFactory;
  private readonly isTypeSupported: (mime: string) => boolean;
  private readonly policies = new Map<string, DesktopCapturePolicyInput>();
  private identity: CaptureIdentity | null = null;
  private local: SourceTrack | null = null;
  private remote: SourceTrack | null = null;
  private readonly trackCleanups = new Map<'local' | 'remote', () => void>();
  private readonly sourceListeners = new Set<(sources: CaptureSources) => void>();
  private readonly stateListeners = new Set<(state: ProducerState) => void>();
  private readonly authUnsubscribe: Unsubscribe | null;
  private run: Run | null = null;
  private disposed = false;
  private current: ProducerState;

  constructor(private readonly deps: ProducerDeps) {
    this.limits = { ...DEFAULT_LIMITS, ...deps.limits };
    this.timers = deps.timers ?? { setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms), clearTimeout: h => globalThis.clearTimeout(h as ReturnType<typeof setTimeout>) };
    this.createGraph = deps.createGraph ?? (() => createWebAudioGraph());
    this.createRecorder = deps.createRecorder ?? ((stream, options) => new MediaRecorder(stream, options));
    this.isTypeSupported = deps.isTypeSupported ?? (mime => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mime));
    this.current = this.blankState();
    this.authUnsubscribe = deps.auth?.onChange((s: AuthState) => {
      if (s.status !== 'signed_in') {
        this.identity = null;
        this.policies.clear();
        if (this.run) void this.terminate(this.run, { drain: false, truncated: true, outcome: 'stopped', reason: 'signed_out' });
      }
    }) ?? null;
  }

  // -------------------------------------------------------------------------------------------
  // Owner integration

  get state(): ProducerState {
    return this.current;
  }

  onStateChange(listener: (state: ProducerState) => void): Unsubscribe {
    this.stateListeners.add(listener);
    return () => void this.stateListeners.delete(listener);
  }

  onSourcesChanged(listener: (sources: CaptureSources) => void): Unsubscribe {
    this.sourceListeners.add(listener);
    return () => void this.sourceListeners.delete(listener);
  }

  /**
   * Latest server-verified state from the owner (call client or web app), on every call, capture,
   * capability or session change. `null` means the owner no longer holds verified state; an active
   * capture stops. Older capture versions for the same call are ignored.
   */
  updatePolicy(input: DesktopCapturePolicyInput | null): void {
    if (this.disposed) return;
    const run = this.run;
    if (input === null || !isValidPolicyInput(input)) {
      this.policies.clear();
      this.identity = null;
      if (run) void this.terminate(run, { drain: false, truncated: true, outcome: 'stopped', reason: input === null ? 'no_policy' : 'invalid_policy' });
      return;
    }
    const identity: CaptureIdentity = { tenant_id: input.tenant_id, user_id: input.user_id, auth_generation: input.auth_generation };
    if (this.identity && !sameIdentity(this.identity, identity)) this.policies.clear();
    this.identity = identity;
    if (run && !sameIdentity(run.identity, identity)) {
      void this.terminate(run, { drain: false, truncated: true, outcome: 'stopped', reason: 'identity_changed' });
      return;
    }
    const prev = this.policies.get(input.call_id);
    if (prev?.capture && input.capture && input.capture.version < prev.capture.version) return;
    this.policies.delete(input.call_id);
    this.policies.set(input.call_id, input);
    while (this.policies.size > 16) this.policies.delete(this.policies.keys().next().value as string);
    if (run && run.callId === input.call_id) this.applyDecision(run);
  }

  /** Caller-owned tracks, on every add, replace or end. The producer only listens; it never stops them. */
  setTracks(next: { local?: SourceTrack | null; remote?: SourceTrack | null }): void {
    if (this.disposed) return;
    let changed = false;
    for (const side of ['local', 'remote'] as const) {
      if (!(side in next)) continue;
      const track = next[side] ?? null;
      if (track === this[side]) continue;
      this.trackCleanups.get(side)?.();
      this.trackCleanups.delete(side);
      this[side] = track;
      if (track) {
        const onChange = () => this.sourcesChanged();
        for (const type of ['ended', 'mute', 'unmute'] as const) track.addEventListener(type, onChange);
        this.trackCleanups.set(side, () => {
          for (const type of ['ended', 'mute', 'unmute'] as const) track.removeEventListener(type, onChange);
        });
      }
      const graph = this.run && !this.run.plan ? this.run.graph : null;
      if (graph) side === 'local' ? graph.setLocal(track) : graph.setRemote(track);
      changed = true;
    }
    if (changed) this.sourcesChanged();
  }

  /** Sign-out, revocation or privacy stop from the owner: nothing more is written. */
  revoke(reason: 'signed_out' | 'revoked' | 'privacy_stop' = 'revoked'): Promise<void> {
    const run = this.run;
    if (!run) return Promise.resolve();
    return this.terminate(run, { drain: false, truncated: true, outcome: 'stopped', reason });
  }

  async dispose(): Promise<void> {
    if (this.disposed) return;
    const run = this.run;
    if (run) await this.terminate(run, { drain: false, truncated: true, outcome: 'stopped', reason: 'disposed' });
    this.disposed = true;
    this.authUnsubscribe?.();
    for (const c of this.trackCleanups.values()) c();
    this.trackCleanups.clear();
    this.local = this.remote = null;
    this.sourceListeners.clear();
    this.stateListeners.clear();
  }

  // -------------------------------------------------------------------------------------------
  // LocalCaptureProducer

  async start(callId: string): Promise<void> {
    if (this.disposed) throw new Error('capture_producer_disposed');
    if (this.run) {
      if (!this.run.plan) throw new Error('capture_already_running');
      await this.run.done; // the previous call's item is finalized before a new one begins
      if (this.run) throw new Error('capture_already_running');
    }
    const policy = this.policies.get(callId) ?? null;
    const decision = decide(policy, callId);
    if (decision.action !== 'record' || !policy || !this.identity) {
      return this.settle('denied', callId, decision.action === 'record' ? 'no_policy' : decision.reason);
    }
    if (!this.isTypeSupported(PRODUCER_MIME)) return this.settle('failed', callId, 'unsupported_codec');
    const sources = this.sources();
    if (!sources.local_microphone && !sources.remote_audio) return this.settle('denied', callId, 'no_sources');

    let resolveDone!: () => void;
    const done = new Promise<void>(r => (resolveDone = r));
    const run: Run = {
      callId,
      identity: { ...this.identity },
      sessionId: null,
      itemId: null,
      maxChunkBytes: this.limits.maxChunkBytes,
      graph: null,
      recorder: null,
      recorderStarted: false,
      recorderDone: false,
      queue: [],
      queuedBytes: 0,
      wantPaused: false,
      bridgePaused: false,
      pendingPause: null,
      plan: null,
      halt: false,
      sessionGone: false,
      bridgeFailure: null,
      acceptedChunks: 0,
      nextSeq: 0,
      droppedBytes: 0,
      started: false,
      finishing: false,
      pumping: false,
      pumpDone: Promise.resolve(),
      wake: null,
      idleWaiters: new Set(),
      drainWaiters: new Set(),
      cleanups: [],
      done,
      resolveDone,
    };
    this.run = run;
    this.current = { ...this.blankState(), phase: 'starting', call_id: callId, sources };
    this.emitState();

    const status = await this.deps.capture.status().catch(() => null);
    if (run.plan) return this.finish(run);
    if (!status) return this.endBeforeBegin(run, 'unavailable', 'bridge_error');
    if (!status.available) return this.endBeforeBegin(run, 'unavailable', status.reason);
    if (!status.consent_recorded) return this.endBeforeBegin(run, 'unavailable', 'consent_not_recorded_on_this_computer');

    try {
      run.graph = this.createGraph();
      run.graph.setLocal(this.local);
      run.graph.setRemote(this.remote);
    } catch {
      return this.endBeforeBegin(run, 'failed', 'audio_graph_failed');
    }
    try {
      run.recorder = this.createRecorder(run.graph.stream, { mimeType: PRODUCER_MIME, audioBitsPerSecond: this.limits.audioBitsPerSecond });
    } catch {
      return this.endBeforeBegin(run, 'failed', 'recorder_unavailable');
    }
    this.attachRecorder(run, run.recorder);

    const recheck = decide(this.policies.get(callId) ?? null, callId);
    if (recheck.action !== 'record') return this.endBeforeBegin(run, 'denied', recheck.reason);
    const begun = this.sources();
    if (!begun.local_microphone && !begun.remote_audio) return this.endBeforeBegin(run, 'denied', 'no_sources');

    let result: CaptureBeginResult;
    try {
      result = await this.deps.capture.begin({ call_id: callId, mime: PRODUCER_MIME, sources: begun });
    } catch (err) {
      if (run.plan) return this.finish(run);
      const code = codeOf(err);
      return this.endBeforeBegin(run, code === 'capture_unavailable' || code === 'capture_consent_required' ? 'unavailable' : 'denied', code);
    }
    run.sessionId = result.session_id;
    run.itemId = result.item_id;
    run.maxChunkBytes = Math.max(1, Math.min(this.limits.maxChunkBytes, result.max_chunk_bytes));
    run.cleanups.push(this.deps.capture.onEvent(e => this.onBridgeEvent(run, e)));
    if (this.run === run) this.patch({ item_id: result.item_id, coverage: result.coverage, coverage_label: coverageLabel(result.coverage) });
    run.started = true;
    // Stopped, revoked or disposed while `begin` was in flight: the empty item is erased.
    if (run.plan) return this.finish(run);

    run.pumping = true;
    run.pumpDone = this.pump(run).finally(() => {
      run.pumping = false;
      for (const w of run.idleWaiters) w();
    });
    const now = this.sources();
    if (!sameSources(now, begun)) this.enqueueSources(run, now);
    const after = decide(this.policies.get(callId) ?? null, callId);
    if (after.action === 'end' || after.action === 'stop') {
      await this.terminate(run, { drain: false, truncated: true, outcome: 'stopped', reason: after.reason });
      return;
    }
    if (run.wantPaused || after.action === 'pause') {
      run.wantPaused = true;
      this.enqueue(run, { kind: 'pause' });
      this.patch({ phase: 'paused' });
      return;
    }
    if (!this.startRecorder(run)) return;
    this.patch({ phase: 'capturing' });
  }

  /** Pause with the server recording pause. Always allowed: pausing never records more. */
  pause(): void {
    const run = this.run;
    if (run) this.doPause(run);
  }

  /** Resume only when the latest server state for this call says recording. */
  resume(): void {
    const run = this.run;
    if (run) this.doResume(run);
  }

  /** End of call: flush the encoder, send the final chunks in order and finalize the spool item. */
  async stop(): Promise<void> {
    const run = this.run;
    if (!run) return;
    await this.terminate(run, { drain: true, truncated: false, outcome: 'stopped', reason: 'call_ended' });
  }

  // -------------------------------------------------------------------------------------------
  // Policy, pause and resume

  private applyDecision(run: Run) {
    if (run.plan) return;
    const d = decide(this.policies.get(run.callId) ?? null, run.callId);
    if (d.action === 'record') this.doResume(run);
    else if (d.action === 'pause') this.doPause(run);
    else if (d.action === 'end') void this.terminate(run, { drain: true, truncated: false, outcome: 'stopped', reason: d.reason });
    else void this.terminate(run, { drain: false, truncated: true, outcome: 'stopped', reason: d.reason as PolicyDenyReason });
  }

  private doPause(run: Run) {
    if (run.plan || run.wantPaused) return;
    run.wantPaused = true;
    if (this.run === run) this.patch({ phase: 'paused' });
    const rec = run.recorder;
    if (run.recorderStarted && rec && rec.state === 'recording') {
      // requestData then pause: MediaRecorder fires the flushed `dataavailable` before `pause`. Once
      // paused, one more requestData flushes anything the encoder still held. Only then is the pause
      // marker queued, so bytes recorded before the pause are written before the spool pauses and are
      // not stranded if the call ends while paused. (Chromium 152 returns an empty blob here; its
      // small post-pause block arrives after resume and is written after the resume marker.)
      let timer: unknown = null;
      const detach = () => {
        rec.removeEventListener('pause', onPaused);
        rec.removeEventListener('dataavailable', onFlushed);
        if (timer !== null) this.timers.clearTimeout(timer);
        timer = null;
      };
      const fire = () => {
        detach();
        if (run.pendingPause !== pending) return;
        run.pendingPause = null;
        this.enqueue(run, { kind: 'pause' });
      };
      // Registered after the queueing listener, so the flushed chunk is queued before the marker.
      const onFlushed = () => fire();
      const onPaused = () => {
        rec.removeEventListener('pause', onPaused);
        if (run.pendingPause !== pending || rec.state !== 'paused') return fire();
        rec.addEventListener('dataavailable', onFlushed);
        try {
          rec.requestData();
        } catch {
          fire();
        }
      };
      const pending = { fire };
      run.pendingPause = pending;
      rec.addEventListener('pause', onPaused);
      timer = this.timers.setTimeout(fire, this.limits.pauseEventTimeoutMs);
      run.cleanups.push(detach);
      try {
        rec.requestData();
        rec.pause();
      } catch {
        fire();
        void this.terminate(run, { drain: false, truncated: true, outcome: 'failed', reason: 'recorder_error' });
      }
    } else if (run.sessionId) {
      this.enqueue(run, { kind: 'pause' });
    }
    // Before `begin` returns, start() sees `wantPaused` and pauses the new item instead of recording.
  }

  private doResume(run: Run) {
    if (run.plan || !run.wantPaused) return;
    const d = decide(this.policies.get(run.callId) ?? null, run.callId);
    if (d.action !== 'record' || !this.identity || !sameIdentity(this.identity, run.identity)) {
      this.patch({ refused: `resume_not_allowed:${d.action === 'record' ? 'identity' : d.reason}` });
      return;
    }
    run.pendingPause?.fire();
    run.wantPaused = false;
    if (this.run === run) this.patch({ phase: run.sessionId ? 'capturing' : 'starting', refused: null });
    if (!run.sessionId) return;
    this.enqueue(run, { kind: 'resume' });
    const rec = run.recorder;
    if (!rec) return;
    if (rec.state === 'paused') {
      try {
        rec.resume();
      } catch {
        void this.terminate(run, { drain: false, truncated: true, outcome: 'failed', reason: 'recorder_error' });
      }
    } else if (!run.recorderStarted) {
      this.startRecorder(run);
    }
  }

  private startRecorder(run: Run): boolean {
    try {
      run.recorder!.start(this.limits.timesliceMs);
      run.recorderStarted = true;
      return true;
    } catch {
      void this.terminate(run, { drain: false, truncated: true, outcome: 'failed', reason: 'recorder_start_failed' });
      return false;
    }
  }

  // -------------------------------------------------------------------------------------------
  // Sources

  private trackLive(t: SourceTrack | null): boolean {
    return !!t && t.readyState === 'live' && !t.muted;
  }

  /** What is being encoded now. A user mute (`enabled = false`) still counts: it is the call as heard. */
  sources(): CaptureSources {
    return { local_microphone: this.trackLive(this.local), remote_audio: this.trackLive(this.remote) };
  }

  private sourcesChanged() {
    const s = this.sources();
    for (const fn of this.sourceListeners) {
      try {
        fn({ ...s });
      } catch {
        /* a failing listener must not break capture */
      }
    }
    this.patch({ sources: s });
    const run = this.run;
    if (run && run.started && !run.halt) this.enqueueSources(run, s);
  }

  /** Reports between two chunks merge, keeping the worst state seen so a short drop still reaches the spool. */
  private enqueueSources(run: Run, s: CaptureSources) {
    const tail = run.queue[run.queue.length - 1];
    if (tail && tail.kind === 'sources') {
      tail.worst = andSources(tail.worst, s);
      tail.last = { ...s };
      return;
    }
    this.enqueue(run, { kind: 'sources', worst: { ...s }, last: { ...s } });
  }

  // -------------------------------------------------------------------------------------------
  // Queue and pump

  private attachRecorder(run: Run, rec: RecorderLike) {
    const onData = (e: Event) => {
      const blob = (e as Event & { data?: Blob }).data;
      if (run.halt || !blob || blob.size === 0) return;
      if (run.queuedBytes + blob.size > this.limits.maxQueuedBytes || run.queue.length >= this.limits.maxQueuedItems) {
        this.addDropped(run, blob.size);
        void this.terminate(run, { drain: false, truncated: true, outcome: 'failed', reason: 'local_queue_overflow' });
        return;
      }
      run.queue.push({ kind: 'chunk', blob });
      run.queuedBytes += blob.size;
      if (this.run === run && run.queuedBytes > this.current.stats.max_queued_bytes) {
        this.patch({ stats: { ...this.current.stats, max_queued_bytes: run.queuedBytes } });
      }
      this.wakePump(run);
    };
    const onError = () => void this.terminate(run, { drain: false, truncated: true, outcome: 'failed', reason: 'recorder_error' });
    const onStop = () => {
      run.recorderDone = true;
      this.wakePump(run);
      if (!run.plan) void this.terminate(run, { drain: true, truncated: true, outcome: 'failed', reason: 'recorder_stopped' });
    };
    rec.addEventListener('dataavailable', onData);
    rec.addEventListener('error', onError);
    rec.addEventListener('stop', onStop);
    run.cleanups.push(() => {
      rec.removeEventListener('dataavailable', onData);
      rec.removeEventListener('error', onError);
      rec.removeEventListener('stop', onStop);
    });
  }

  private enqueue(run: Run, m: Marker) {
    if (run.halt) return;
    const tail = run.queue[run.queue.length - 1];
    // A pause immediately undone before it reached the spool cancels out.
    if (m.kind === 'resume' && tail?.kind === 'pause') {
      run.queue.pop();
      return;
    }
    if (run.queue.length >= this.limits.maxQueuedItems) {
      void this.terminate(run, { drain: false, truncated: true, outcome: 'failed', reason: 'local_queue_overflow' });
      return;
    }
    run.queue.push(m);
    this.wakePump(run);
  }

  private wakePump(run: Run) {
    const w = run.wake;
    run.wake = null;
    w?.();
  }

  private releaseWaiters(run: Run) {
    this.wakePump(run);
    for (const w of [...run.idleWaiters, ...run.drainWaiters]) w();
    run.idleWaiters.clear();
    run.drainWaiters.clear();
  }

  /** Next item to send. While the spool is paused, chunks wait for the next resume. */
  private nextItem(run: Run): QueueItem | null {
    const head = run.queue[0];
    if (!head) return null;
    if (head.kind === 'chunk' && run.bridgePaused) {
      const at = run.queue.findIndex(i => i.kind === 'resume');
      if (at !== -1) return run.queue.splice(at, 1)[0]!;
      const markerAt = run.queue.findIndex(i => i.kind === 'sources');
      if (markerAt !== -1) return run.queue.splice(markerAt, 1)[0]!;
      if (run.plan && run.recorderDone) {
        // Ended while paused: encoder output after the pause point cannot be written. Count it.
        for (const i of run.queue) if (i.kind === 'chunk') this.addDropped(run, i.blob.size);
        run.queue = run.queue.filter(i => i.kind !== 'chunk');
        run.queuedBytes = 0;
        return run.queue.shift() ?? null;
      }
      return null;
    }
    return run.queue.shift()!;
  }

  private async pump(run: Run): Promise<void> {
    while (!run.halt) {
      const item = this.nextItem(run);
      if (!item) {
        for (const w of run.idleWaiters) w();
        run.idleWaiters.clear();
        await new Promise<void>(r => (run.wake = r));
        continue;
      }
      const ok = await this.send(run, item);
      if (!ok) return;
    }
  }

  private async send(run: Run, item: QueueItem): Promise<boolean> {
    const sid = run.sessionId!;
    const capture = this.deps.capture;
    try {
      if (item.kind === 'chunk') {
        const size = item.blob.size;
        let sent = 0;
        try {
          const bytes = new Uint8Array(await item.blob.arrayBuffer());
          // Oversized recorder output is split; the spool stores one contiguous byte stream either way.
          // A slice (copy) keeps each IPC message to its own bytes.
          for (let at = 0; at < bytes.byteLength && !run.halt; at += run.maxChunkBytes) {
            const piece = bytes.byteLength <= run.maxChunkBytes ? bytes : bytes.slice(at, at + run.maxChunkBytes);
            if (!(await this.appendWithRetry(run, sid, piece))) break;
            sent += piece.byteLength;
          }
        } finally {
          run.queuedBytes -= size;
          if (sent < size) this.addDropped(run, size - sent);
        }
        return !run.halt && sent === size;
      }
      if (item.kind === 'sources') {
        if (!sameSources(item.worst, item.last)) await this.reportSources(run, sid, item.worst);
        if (!run.halt) await this.reportSources(run, sid, item.last);
        return !run.halt;
      }
      if (item.kind === 'pause') {
        await capture.pause(sid);
        run.bridgePaused = true;
        return true;
      }
      await capture.resume(sid);
      run.bridgePaused = false;
      return true;
    } catch (err) {
      this.onBridgeError(run, codeOf(err));
      return false;
    }
  }

  private async reportSources(run: Run, sid: string, sources: CaptureSources) {
    const { coverage } = await this.deps.capture.reportSources({ session_id: sid, sources: { ...sources } });
    if (this.run === run) this.patch({ coverage, coverage_label: coverageLabel(coverage) });
  }

  /**
   * Sequential append; resolves true when the spool accepted the bytes. Backpressure retries the same
   * sequence number and bytes until the spool drains, within a bounded total wait.
   */
  private async appendWithRetry(run: Run, sid: string, data: Uint8Array): Promise<boolean> {
    let waited = 0;
    let backoff = this.limits.backoffInitialMs;
    for (;;) {
      if (run.halt) return false;
      const seq = run.nextSeq;
      try {
        const res = await this.deps.capture.append({ session_id: sid, seq, data });
        // The spool accepted it even if this run has since been halted; keep the count honest.
        if (res.next_seq !== seq + 1) {
          run.nextSeq = res.next_seq;
          run.acceptedChunks++;
          this.onBridgeError(run, 'sequence_desync');
          return true;
        }
        run.nextSeq = res.next_seq;
        run.acceptedChunks++;
        if (this.run === run) {
          const st = this.current.stats;
          this.patch({ stats: { ...st, chunks_sent: st.chunks_sent + 1, bytes_sent: st.bytes_sent + data.byteLength } });
        }
        if (run.halt) return true;
        if (res.backpressure) await this.waitDrained(run, this.limits.backoffMaxMs);
        return true;
      } catch (err) {
        if (run.halt) return false;
        const code = codeOf(err);
        if (code !== 'capture_backpressure') {
          this.onBridgeError(run, code);
          return false;
        }
        if (waited >= this.limits.maxBackpressureWaitMs) {
          this.onBridgeError(run, 'stalled');
          return false;
        }
        if (this.run === run) {
          const st = this.current.stats;
          this.patch({ stats: { ...st, backpressure_retries: st.backpressure_retries + 1 } });
        }
        await this.waitDrained(run, backoff);
        waited += backoff;
        backoff = Math.min(backoff * 2, this.limits.backoffMaxMs);
      }
    }
  }

  private waitDrained(run: Run, ms: number): Promise<void> {
    if (run.halt) return Promise.resolve();
    return new Promise<void>(resolve => {
      const finish = () => {
        this.timers.clearTimeout(timer);
        run.drainWaiters.delete(finish);
        resolve();
      };
      const timer = this.timers.setTimeout(finish, ms);
      run.drainWaiters.add(finish);
    });
  }

  private waitIdle(run: Run, ms: number): Promise<void> {
    if (run.halt || !run.pumping) return Promise.resolve();
    return new Promise<void>(resolve => {
      const finish = () => {
        this.timers.clearTimeout(timer);
        run.idleWaiters.delete(finish);
        resolve();
      };
      const timer = this.timers.setTimeout(finish, ms);
      run.idleWaiters.add(finish);
      this.wakePump(run); // an idle pump re-checks and reports idle
    });
  }

  private onBridgeEvent(run: Run, e: CaptureEvent) {
    if (e.type === 'item_changed') {
      if (this.run === run && e.item.item_id === run.itemId && e.item.state !== 'discarded' && e.item.state !== 'local_copy_removed') {
        this.patch({ coverage: e.item.coverage, coverage_label: e.item.coverage_label });
      }
      return;
    }
    if (e.session_id !== run.sessionId) return;
    if (e.type === 'drained') {
      for (const w of [...run.drainWaiters]) w();
      return;
    }
    this.desktopStopped(run, `desktop_${e.reason}`);
  }

  private onBridgeError(run: Run, code: string) {
    if (DESKTOP_STOPPED_CODES.has(code)) return this.desktopStopped(run, `desktop_${code}`);
    run.bridgeFailure ??= code;
    void this.terminate(run, { drain: false, truncated: true, outcome: 'failed', reason: `bridge_${code}` });
  }

  /** Main stopped or finalized the session itself; no further bridge calls are made for it. */
  private desktopStopped(run: Run, reason: string) {
    if (run.sessionGone) return;
    run.sessionGone = true;
    void this.terminate(run, { drain: false, truncated: true, outcome: 'stopped', reason });
  }

  // -------------------------------------------------------------------------------------------
  // Termination

  private terminate(run: Run, plan: Plan): Promise<void> {
    if (run.plan) {
      // Already ending. A stricter request (privacy, revocation, failure) stops writes now.
      if (!plan.drain && run.plan.drain) {
        run.plan = { drain: false, truncated: true, outcome: plan.outcome === 'failed' ? 'failed' : run.plan.outcome, reason: plan.reason };
        run.halt = true;
        this.releaseWaiters(run);
      }
      return run.done;
    }
    run.plan = plan;
    if (!plan.drain) {
      run.halt = true;
      this.releaseWaiters(run);
    }
    if (this.run === run) this.patch({ phase: 'stopping' });
    // Before start() has a session it owns the run; it calls finish() at its next checkpoint.
    if (run.started) void this.finish(run);
    return run.done;
  }

  private async finish(run: Run): Promise<void> {
    if (run.finishing) return run.done;
    run.finishing = true;
    try {
      await this.stopRecorder(run);
      if (run.plan!.drain && !run.halt) await this.waitIdle(run, this.limits.endDrainTimeoutMs);
      run.halt = true;
      this.releaseWaiters(run);
      await run.pumpDone;
      for (const i of run.queue) if (i.kind === 'chunk') this.addDropped(run, i.blob.size);
      run.queue = [];
      run.queuedBytes = 0;

      const plan = run.plan!;
      let item: SpoolItemView | null = null;
      const truncated = plan.truncated || run.droppedBytes > 0;
      if (run.sessionId && !run.sessionGone) {
        const sid = run.sessionId;
        try {
          if (run.acceptedChunks === 0) {
            await this.deps.capture.abort(sid);
          } else {
            // The item ends before the call or lost audio: a final "no sources" report keeps the spool
            // from presenting it as covering the whole call.
            if (truncated) await this.deps.capture.reportSources({ session_id: sid, sources: { ...NO_SOURCES } });
            item = await this.deps.capture.finalize(sid);
          }
        } catch (err) {
          const code = codeOf(err);
          if (!DESKTOP_STOPPED_CODES.has(code)) run.bridgeFailure ??= `finalize_${code}`;
        }
      }
      if (this.run === run) {
        const plan2 = run.plan!;
        const extra = run.bridgeFailure && plan2.reason !== `bridge_${run.bridgeFailure}` ? `;${run.bridgeFailure}` : '';
        this.patch({
          phase: plan2.outcome === 'failed' || run.bridgeFailure ? 'failed' : 'stopped',
          reason: plan2.reason + extra,
          truncated: (plan2.truncated || run.droppedBytes > 0) && run.acceptedChunks > 0,
          item,
          ...(item ? { coverage: item.coverage, coverage_label: item.coverage_label } : {}),
        });
      }
    } finally {
      await this.teardown(run);
      if (this.run === run) this.run = null;
      run.resolveDone();
    }
  }

  private async stopRecorder(run: Run) {
    const rec = run.recorder;
    if (!rec || rec.state === 'inactive') {
      run.recorderDone = true;
      return;
    }
    await new Promise<void>(resolve => {
      let timer: unknown = null;
      const done = () => {
        rec.removeEventListener('stop', done);
        if (timer !== null) this.timers.clearTimeout(timer);
        resolve();
      };
      rec.addEventListener('stop', done);
      timer = this.timers.setTimeout(done, this.limits.stopFlushTimeoutMs);
      try {
        rec.stop();
      } catch {
        done();
      }
    });
    run.recorderDone = true;
    this.wakePump(run);
  }

  private async teardown(run: Run) {
    run.halt = true;
    run.pendingPause = null;
    for (const c of run.cleanups.splice(0)) {
      try {
        c();
      } catch {
        /* keep tearing down */
      }
    }
    this.releaseWaiters(run);
    run.queue = [];
    run.queuedBytes = 0;
    const rec = run.recorder;
    run.recorder = null;
    if (rec && rec.state !== 'inactive') {
      try {
        rec.stop();
      } catch {
        /* already stopped */
      }
    }
    const graph = run.graph;
    run.graph = null;
    await graph?.close().catch(() => {});
  }

  /** Ends a run that never got a bridge session (denied, unavailable or failed before `begin`). */
  private async endBeforeBegin(run: Run, phase: 'denied' | 'unavailable' | 'failed', reason: string): Promise<void> {
    run.plan = { drain: false, truncated: false, outcome: phase === 'failed' ? 'failed' : 'stopped', reason };
    run.finishing = true;
    await this.teardown(run);
    if (this.run === run) {
      this.run = null;
      this.patch({ phase, reason });
    }
    run.resolveDone();
  }

  private addDropped(run: Run, n: number) {
    if (n <= 0) return;
    run.droppedBytes += n;
    if (this.run === run) this.patch({ stats: { ...this.current.stats, dropped_bytes: run.droppedBytes } });
  }

  // -------------------------------------------------------------------------------------------
  // State

  private blankState(): ProducerState {
    return {
      phase: 'idle',
      call_id: null,
      reason: null,
      item_id: null,
      coverage: null,
      coverage_label: null,
      sources: this.sources(),
      truncated: false,
      item: null,
      stats: { chunks_sent: 0, bytes_sent: 0, backpressure_retries: 0, dropped_bytes: 0, max_queued_bytes: 0 },
      refused: null,
    };
  }

  private settle(phase: 'denied' | 'failed', callId: string, reason: string) {
    this.current = { ...this.blankState(), phase, call_id: callId, reason };
    this.emitState();
  }

  private patch(p: Partial<ProducerState>) {
    this.current = { ...this.current, ...p };
    this.emitState();
  }

  private emitState() {
    const snapshot = Object.freeze({ ...this.current, sources: { ...this.current.sources }, stats: { ...this.current.stats } });
    this.current = snapshot;
    for (const fn of this.stateListeners) {
      try {
        fn(snapshot);
      } catch {
        /* a failing listener must not break capture */
      }
    }
  }
}
